import { fromJS } from 'immutable';

// Constants
import { CONSTANTS } from './constants';
import { api, METHOD, REQUEST_URL, formatPattern } from '../../utils';
import { getLegendsVisibility, getSavedChartMode, getUserPreferences, selectedLangageSelector } from '../app/selector';
import { getMetersListSortedForMonitoring } from '../meters/selectors';
import moment from 'moment';
import { API_URL } from '../../utils/api/constants';
import keycloak from '../../utils/keycloak';

export const onMonitoringIsLoading = isLoading => ({
    type: CONSTANTS.ON_MONITORING_IS_LOADING,
    payload: { isLoading }
});

export const onMonitoringIsLive = isLive => ({
    type: CONSTANTS.ON_MONITORING_IS_LIVE,
    payload: { isLive }
});

export const onMonitoringSetLastTs = lastTs => ({
    type: CONSTANTS.ON_MONITORING_SET_LAST_TS,
    payload: { lastTs }
});

export const onMonitoringSetBounds = (from, to) => ({
    type: CONSTANTS.ON_MONITORING_SET_BOUNDS,
    payload: { from, to }
});

export const onClearRawData = data => ({
    type: CONSTANTS.ON_CLEAR_RAW_DATA,
    payload: { data }
});

const onRawData = data => ({
    type: CONSTANTS.ON_RAW_DATA,
    payload: { data }
});

// ========= THUNKS ============
/**
 * fetchMonitoringRawData
 * - Fetching all device types
 * @since 2.7.11
 *
 *
 * @returns {Promise}
 */
export const fetchMonitoringRawData = meterIds => (dispatch, getState) => {

    const meters = meterIds.filter(m => Number.isInteger(m));

    if (meters.length <= 0) {
        return Promise.resolve();
    }

    // meterId, fromTime, toTime, depth, count
    const from = getState().monitoring.get('from');
    const to = getState().monitoring.get('to');
    const ts = getState().monitoring.get('setTimestamp');
    const isLive = getState().monitoring.get('isLive');
    const lastTs = getState().monitoring.get('lastTs');
    const sw = getState().switches.get('selectedSwitch');
    const chartMode = getSavedChartMode(getState());
    const visiblePeriod = getState().app.get('visiblePeriod');
    const visibility = getLegendsVisibility(getState());
    const loadOnOff = visibility ? visibility[0] : false;

    const currentTs = moment().valueOf();
    const sides = Math.round((from - to) / 2);

    let fromTs = 0;
    let toTs = 0;

    if(isLive){
        toTs = currentTs;
        fromTs = lastTs === 0 ? toTs - from : lastTs;
    } else {
        const timeDif = ts > 0 ? currentTs - ts : 0;
        toTs = currentTs - to - timeDif + sides;
        fromTs = currentTs - from - timeDif - sides;
    }
    const chartMinTs = currentTs - from;
    const chartMaxTs = currentTs - to;
    const len = Math.round((from - to + (isLive ? 0 : 2 * sides)) / 250); // total record count
    let depth = Math.round(Math.log2(len / 1000));
    depth = (depth < 0 || !Number.isInteger(depth)) ? 0 : depth;

    const count = 10000 * meters.length; // max

    dispatch(onMonitoringIsLoading(true));

    // Reset data if timestamps are not logical, to avoid reproducing WA-6371 with white screen
    // This is dirty since it erases all the current user's configuration but is safer to correct this unreproducible behavior
    const differenceOfTwoYears = toTs - fromTs > 1000 * 60 * 60 * 24 * 750;
    const before2000 = toTs < 946684800000 || fromTs < 946684800000;
    const after2100 = toTs > 4102444800000 || fromTs > 4102444800000;
    if (differenceOfTwoYears || before2000 || after2100)  {
        localStorage.clear();
        window.location.reload();
        return;
    }

    return api(`${formatPattern(REQUEST_URL.RAW_DATA, {meterIds: meters})}?depth=${2 ** depth}&count=${count || 0}&fromTime=${fromTs || 0}&toTime=${toTs || 0}&chartMinTs=${chartMinTs || 0}&chartMaxTs=${chartMaxTs || 0}&sw=${sw}&loadOnOff=${loadOnOff}&locale=${selectedLangageSelector(getState())}&viewSwitch=${chartMode}`, METHOD.GET)
        .then(res => {
            if (res && res.ok) {
                // console.log(res.body);
                // check if something is changed meanwhile:

                const selectedMeterIds = getUserPreferences(getState()).get('selectedMeterIds');

                const newLastTs = getState().monitoring.get('lastTs');
                const newMeterIds = selectedMeterIds !== undefined ? selectedMeterIds.split(',').map(id => parseInt(id, 10)) : [];
                const newSw = getState().switches.get('selectedSwitch');
                const newVisiblePeriod = getState().app.get('visiblePeriod');
                const newFrom = getState().monitoring.get('from');
                const newTo = getState().monitoring.get('to');
                dispatch(onMonitoringIsLoading(false));
                // console.log(visiblePeriod, newVisiblePeriod, from, newFrom, to, newTo);
                // console.log(res.body);

                if (newMeterIds.length === 0){
                    dispatch(onRawData({recognitions: [], alwaysOn: [], powerData: []}));    // return empty object when no meter selected
                }
                else if(
                    newLastTs === lastTs &&
                    newSw === sw &&
                    newFrom === from &&
                    newTo === to &&
                    meterIds.length === newMeterIds.length && meterIds.every((value, index) => value === newMeterIds[index]) &&
                    visiblePeriod === newVisiblePeriod
                    || newMeterIds === undefined
                    || newMeterIds.length === 0
                ) {
                    dispatch(onRawData(fromJS(res.body)));
                } else dispatch(fetchMonitoringRawData(newMeterIds));
            }
            return res;
        })
        .catch(err => {
            dispatch(onMonitoringIsLoading(false));
            throw err;
        });
};

/**
* getTotalKwh
* - Fetching total usage (kwh)
* @since 2.7.4
*
*
* @returns {Promise}
*/
export const getTotalKwh = (meterId, from, to) => (dispatch, getState) => api(
    `${formatPattern(
        REQUEST_URL.KWH_BY_METER_ID,
        {meterId}
    )}?from=${from}&to=${to}`
    , METHOD.GET)
    .then(res => {
        if (res && res.ok) {
            return res.body;
        }
    })
    .catch(err => {
        throw err;
    });

/**
 * getDeviceConsumption
 * - Fetching total usage (kwh)
 * @since 2.7.5
 *
 *
 * @returns {Promise}
 */
export const getDeviceConsumption = (meterId, from, to, alwaysOn = false) => (dispatch, getState) => api(
    `${formatPattern(
        REQUEST_URL.DEVICE_CONSUMPTION_FOR_METER_ID,
        {meterId}
    )}?from=${from}&to=${to}&alwayson=${alwaysOn}`
    , METHOD.GET)
    .then(res => {
        if (res && res.ok) {
            return res.body;
        }
    })
    .catch(err => {
        throw err;
    });

/**
 * getCsvFile
 * - Fetching csv file for time range
 * @since 2.7.4
 *
 *
 * @returns {Promise}
 */
export const getCsvFile = (meterId, from, to, rate) => (dispatch, getState) => {
    const { token } = keycloak.getTokenStorage();
    const currentTimeZone = new Intl.DateTimeFormat().resolvedOptions().timeZone;
    window.open(`${API_URL}${formatPattern(
        REQUEST_URL.CSV_REPORT,
        {meterId}
    )}?X-Auth-Token=${token || ''}&fromTime=${from}&toTime=${to}&rate=${rate}&timeZone=${currentTimeZone}`);
};

/**
 * getExcelFile
 * - Fetching Excel file for the two periods*
 *
 * @returns {Promise}
 */
export const getExcelFile = (meterIds, fromP1, toP1, fromP2, toP2) => (dispatch, getState) => {
    const { token } = keycloak.getTokenStorage();
    const lang = getState().app.get('lang');
    window.open(`${API_URL}${formatPattern(
        REQUEST_URL.EXCEL_REPORT,
        {meterIds}
    )}?X-Auth-Token=${token || ''}&fromTimeP1=${fromP1}&toTimeP1=${toP1}&fromTimeP2=${fromP2}&toTimeP2=${toP2}&lang=${lang}`);
};

export const addUrlAndToken = (props) => (_, getState) => {
    const { token } = keycloak.getTokenStorage();

    if (!token) {
        return;
    }

    const metersData = getMetersListSortedForMonitoring(getState());
    const meters = metersData && metersData.toJS ? metersData.toJS() : [];
    const selectedMeter = getUserPreferences(getState()).get('selectedMeterIds').split(',').map(id => parseInt(id, 10));
    const meter = meters.filter(item => item.id === parseInt(selectedMeter[0], 10));

    let homeId;

    if (meter && meter[0] && meter[0].home) {
        homeId = meter[0].home.id;
    } else {
        return props;
    }

    const headers = {...props.headers, 'X-Auth-Token': token};
    const action = `${API_URL}${formatPattern(
        REQUEST_URL.HOME_IMPORT_CSV,
        {homeId}
    )}`;

    return {...props, headers, action};
};
